import { useNavigate } from "react-router-dom"
import { useQuery } from "urql"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { SourceQuery, EventsQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

type EventItem = ResultOf<typeof EventsQuery>["events"]["nodes"][number]

const eventColumns: Column<EventItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Received",
    cell: (row) => <TimeAgo date={row.receivedAt} />,
  },
]

export function ShareSource({ id }: { id: string }) {
  const navigate = useNavigate()
  const [result] = useQuery({ query: SourceQuery, variables: { id } })
  const [events] = useQuery({
    query: EventsQuery,
    variables: { sourceId: id, limit: 20, offset: 0 },
  })

  const source = result.data?.source

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!source) {
    return <EmptyState title="Source not found" />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{source.name}</h1>
        <StatusBadge status={source.status} />
      </div>

      <Card className="border-border/30">
        <CardHeader>
          <CardTitle className={sectionTitleClass}>Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel className="mb-1">Slug</FieldLabel>
              <code className="font-mono text-sm">{source.slug}</code>
            </div>
            <div>
              <FieldLabel className="mb-1">Provider</FieldLabel>
              <div className="capitalize">{source.providerType}</div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={source.createdAt} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Recent Events</h2>
        <DataTable
          columns={eventColumns}
          data={events.data?.events.nodes ?? []}
          keyFn={(row) => row.id}
          loading={events.fetching}
          emptyTitle="No events"
          onRowClick={(row) => navigate(`?${new URLSearchParams(window.location.search).toString().replace(/type=[^&]+/, "type=event").replace(/id=[^&]+/, `id=${row.id}`)}`)}
        />
      </div>
    </div>
  )
}
